import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { storyContent } from "@/data/dummy";
import { useIsMobile } from "@/hooks/use-mobile";

export const ProductsSection = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollInfo = () => {
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const maxScrollLeft = scrollWidth - clientWidth;
      
      setScrollPosition(scrollLeft);
      setMaxScroll(maxScrollLeft);
    };

    // Initial calculation
    updateScrollInfo();

    container.addEventListener('scroll', updateScrollInfo);
    window.addEventListener('resize', updateScrollInfo);

    return () => {
      container.removeEventListener('scroll', updateScrollInfo);
      window.removeEventListener('resize', updateScrollInfo);
    };
  }, []);

  // Calculate scroll progress (0 to 1)
  const scrollProgress = maxScroll > 0 ? scrollPosition / maxScroll : 0;
  
  // Calculate current dot based on scroll position
  const getCurrentDot = () => {
    if (scrollProgress < 0.33) return 0;
    if (scrollProgress < 0.67) return 1;
    return 2;
  };

  const scrollToPosition = (dotIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    let targetProgress;
    switch(dotIndex) {
      case 0:
        targetProgress = 0;
        break;
      case 1:
        targetProgress = 0.5;
        break;
      case 2:
        targetProgress = 1;
        break;
      default:
        targetProgress = 0;
    }
    
    const targetScroll = targetProgress * maxScroll;
    container.scrollTo({ left: targetScroll, behavior: 'smooth' });
  };
  return (
    <motion.section
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="py-20 px-4"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="mb-12 text-center space-y-4">
            <h2 className="font-sans font-normal tracking-tight text-3xl md:text-4xl text-foreground">Products We're Testing</h2>
            <p className="text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed font-sans font-light">
              The first five baby formula products in our independent testing pipeline
            </p>
          </div>
          
          {/* Product Carousel */}
          <div className="flex flex-col items-center">
            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide max-w-full pl-4 pr-4"
            >
              {storyContent.images.map((image, index) => (
                <motion.a
                  key={index}
                  href={image.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-xl shadow-card border border-border aspect-square overflow-hidden group flex-shrink-0 w-48"
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </motion.a>
              ))}
            </div>
            
            {/* Functional Scroll Indicators */}
            {isMobile && (
            <div className="flex justify-center mt-4 space-x-2">
              {[0, 1, 2].map((dotIndex) => {
                const currentDot = getCurrentDot();
                const isActive = currentDot === dotIndex;
                const isCenter = dotIndex === 1;
                
                return (
                  <button
                    key={dotIndex}
                    onClick={() => scrollToPosition(dotIndex)}
                    className={`transition-all duration-300 rounded-full ${
                      isActive 
                        ? isCenter 
                          ? 'w-6 h-2 bg-primary' 
                          : 'w-3 h-3 bg-primary'
                        : 'w-2 h-2 bg-primary/30 hover:bg-primary/50'
                    }`}
                    aria-label={`Scroll to ${dotIndex === 0 ? 'start' : dotIndex === 1 ? 'middle' : 'end'}`}
                  />
                );
              })}
            </div>
            )}
          </div>

          {/* Sponsor Test Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-base text-muted-foreground max-w-4xl mx-auto leading-relaxed font-sans font-light">
              Want to test a specific product? You can sponsor a test for $3,000 by emailing{" "}
              <a 
                href="mailto:team@fixtheformula.org?subject=Sponsor formula test"
                className="text-primary hover:text-primary/80 transition-colors underline"
              >
                team@fixtheformula.org
              </a>
              {" "}with the subject line "Sponsor formula test," and we'll make it happen!
            </p>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};